import { getToken } from 'firebase/messaging';
import { messaging } from './firebase';
import { saveFcmToken, clearFcmTokens } from './userService';

const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY || 'BO0fPsGuqNx7jgS537nrIfiI6B38sH5UO_LDw9Zx3jK15tgmISB6afBNUz17IzPo-mGP4LtuTQPavZmUr9GBZpc';

/**
 * Resolve the app's single service worker registration (sw.js, which also
 * handles push). Without passing this explicitly, getToken() auto-registers
 * a separate firebase-messaging-sw.js at the same scope, which caused two
 * service workers to compete for control and duplicate push delivery on iOS.
 */
const getSwRegistration = async () => {
  if (!('serviceWorker' in navigator)) return undefined;
  try {
    return await navigator.serviceWorker.ready;
  } catch {
    return undefined;
  }
};

/**
 * Request notification permission and save the FCM token to the user's doc.
 * Returns the token string, or null if permission was denied or unavailable.
 */
export const enablePushNotifications = async (uid) => {
  if (!messaging) {
    throw new Error('Push messaging not initialized. On iPhone, open ServeSync from your home screen icon (not Safari).');
  }
  if (!VAPID_KEY) {
    throw new Error('Push notifications are not configured (missing VAPID key).');
  }
  const permission = await Notification.requestPermission();
  if (permission === 'denied') {
    throw new Error('Notifications are blocked. Go to iPhone Settings → Notifications → ServeSync and allow notifications, then try again.');
  }
  if (permission !== 'granted') {
    // User dismissed the prompt without choosing
    return null;
  }
  try {
    const serviceWorkerRegistration = await getSwRegistration();
    const token = await getToken(messaging, { vapidKey: VAPID_KEY, serviceWorkerRegistration });
    if (!token) throw new Error('Could not get a push token. Try closing and reopening the app, then try again.');
    await saveFcmToken(uid, token);
    return token;
  } catch (err) {
    console.error('Failed to get FCM token:', err);
    throw new Error(err.message || 'Failed to register for push notifications.');
  }
};

/**
 * Disable push notifications — clears stored tokens so the server stops sending.
 */
export const disablePushNotifications = async (uid) => {
  await clearFcmTokens(uid);
};

/**
 * Silently refresh the FCM token on login if permission is already granted.
 * Does not prompt the user.
 */
export const refreshFcmToken = async (uid) => {
  if (!messaging || !VAPID_KEY || Notification.permission !== 'granted') return;
  try {
    const serviceWorkerRegistration = await getSwRegistration();
    const token = await getToken(messaging, { vapidKey: VAPID_KEY, serviceWorkerRegistration });
    if (token) await saveFcmToken(uid, token);
  } catch (err) {
    console.error('Failed to refresh FCM token:', err);
  }
};
